/**
 * Chest reward table (HEX-140).
 *
 * Replaces the flat "+25 gold" chest (playtest D4 — lacklustre) with a weighted
 * table of varied rewards: tier-scaled gold / a card / a specialist die / a
 * flavour beat. Mirrors HEX-137's pure-table pattern (`events/eventTable.ts`):
 * the table is data so rewards can be added without touching `ChestFlow`, and
 * the per-reward maths (gold scaling, die pick, flavour pick) live here so they
 * unit-test without a DOM.
 *
 * Deterministic per chest: the host derives a seeded RNG from `(coords,
 * crossings)` via `chestSeed`, the same nonce shape the danger-event roll uses,
 * so a given chest replays. The card/die wiring (the HEX-149 acquisition drawer:
 * place-or-decline beside the loadout) lives INLINE in `ChestFlow` — deliberately
 * not factored into a shared reward helper (HEX-141 touches the same surface).
 */

import type { DieType } from '../combat/dice';
import { SPECIALIST_DICE } from '../combat/diceRoster';

/** The reward kinds a chest can roll (ADR-0012 §chest; HEX-140). */
export type ChestRewardKind = 'gold' | 'card' | 'die' | 'flavour';

export interface ChestRewardDef {
  readonly kind: ChestRewardKind;
  /** Relative weight in the table; need not sum to any particular total. */
  readonly weight: number;
  /** Modal title for the reward beat. */
  readonly title: string;
}

/**
 * Starter reward table (4 kinds). Gold is the common bread-and-butter find;
 * card is the prized treasure; die the rarer specialist boon; flavour the
 * "empty chest" pressure valve that keeps a chest from always paying out.
 */
export const CHEST_REWARD_TABLE: readonly ChestRewardDef[] = [
  { kind: 'gold', weight: 4, title: 'Coin' },
  { kind: 'card', weight: 3, title: 'A find' },
  { kind: 'die', weight: 1, title: 'A weighted die' },
  { kind: 'flavour', weight: 2, title: 'Empty' },
];

/**
 * Pick a reward from the weighted table given a normalised roll in `[0, 1)`
 * (one `Rng()` draw). Deterministic: the same roll against the same table always
 * lands on the same reward. A roll at or beyond the total (defensive) falls to
 * the last entry. Mirror of `pickChanceEvent`.
 */
export function pickChestReward(
  roll: number,
  table: readonly ChestRewardDef[] = CHEST_REWARD_TABLE,
): ChestRewardDef {
  const total = table.reduce((sum, r) => sum + r.weight, 0);
  let acc = roll * total;
  for (const reward of table) {
    acc -= reward.weight;
    if (acc < 0) return reward;
  }
  return table[table.length - 1] as ChestRewardDef;
}

/**
 * Deterministic seed for a chest's reward roll — mixes coords + footfall so a
 * chest replays. Mirrors `eventSeed` (lifecycle/dangerEvent) with a chest salt
 * so a chest and a danger event on the same coords never share a stream. Feeds
 * `createRng` (src/combat/dice.ts).
 */
export function chestSeed(q: number, r: number, crossings: number): number {
  return (
    (Math.imul(q, 73856093) ^
      Math.imul(r, 19349663) ^
      Math.imul(crossings, 83492791) ^
      0xc4e57b1) >>>
    0
  );
}

// --- Gold -------------------------------------------------------------------

/** Base gold in a chest before the distance-tier bonus. */
export const CHEST_BASE_GOLD = 20;

/** Per-tier gold step added to a chest. */
export const CHEST_TIER_STEP = 10;

/**
 * Gold in a chest, scaled by the tile's distance tier (1–5): deeper-frontier
 * chests pay more, holding the "distance is the only progression axis"
 * thesis (ADR-0018 §1). Tier 1 → 20, tier 5 → 60. Mirror of `cacheGold`.
 */
export function chestGold(tier: number): number {
  return CHEST_BASE_GOLD + (tier - 1) * CHEST_TIER_STEP;
}

// --- Die --------------------------------------------------------------------

/** Pick a specialist die from the roster given a normalised roll in `[0, 1)`.
 * The caller clones it before handing it to the character (owned dice are stored
 * by value). Uniform over the specialist roster — chests don't tier-scale dice. */
export function pickChestDie(roll: number): DieType {
  const i = Math.min(SPECIALIST_DICE.length - 1, Math.floor(roll * SPECIALIST_DICE.length));
  return SPECIALIST_DICE[i] as DieType;
}

// --- Flavour ----------------------------------------------------------------

/** Flavour lines for an empty chest; one is picked from the RNG stream. */
export const CHEST_FLAVOURS: readonly string[] = [
  'The lid groans open on bare, dust-furred boards. Someone got here first.',
  'Empty — but for a pressed flower and a name you cannot read, scratched inside the lid.',
  'Hinges shriek, the chest yawns wide, and offers you precisely nothing.',
];

/** Pick a flavour line from a normalised roll in `[0, 1)`. */
export function pickChestFlavour(roll: number): string {
  const i = Math.min(CHEST_FLAVOURS.length - 1, Math.floor(roll * CHEST_FLAVOURS.length));
  return CHEST_FLAVOURS[i] as string;
}
